/**
 * Reads an image file's intrinsic pixel dimensions in the browser, so an oversized logo can be rejected
 * before it is ever uploaded.
 *
 * Deliberately uses an `<img>` and an object URL rather than `createImageBitmap`. Both report the same
 * numbers, but `createImageBitmap` fully rasterises the image to do it — and the whole point here is to
 * catch images so large that rasterising them is the problem. An `<img>` that is never inserted into the
 * document resolves `onload` once the header has been parsed and leaves rasterisation to paint time,
 * which never comes.
 *
 * Resolves `{ width: 0, height: 0 }` for anything the browser cannot decode (a renamed archive, a
 * corrupt file) instead of rejecting, so callers get one validation path rather than two.
 */
export function readImageDimensions(file: Blob): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    const settle = (size: { width: number; height: number }) => {
      URL.revokeObjectURL(url);
      resolve(size);
    };

    image.onload = () => settle({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => settle({ width: 0, height: 0 });
    image.src = url;
  });
}
